import React, {useState} from 'react'
import moment from 'moment'
import {Badge} from 'react-bootstrap'
import {Tooltip} from 'react-tooltip'
import {useQuery} from '@apollo/client'
import {BadgeStatePolicy} from './BadgeStatePolicy'
import {ModalPreview} from '../../policies/components/ModalPreview'
import {getDocumentVariant, getPaymentVariant, METAL_LEVELS} from '../../../helpers/consts'
import {GET_ALL_POLICIES_STATUSES} from '../../../gql/queries/policiesQueries'
import {getColorByStatus, getTitleByStatus} from '../../shared/utils'

export interface FamilyItem {
  pk: number
  parentPk: number | null
  depth: number
  policyName: string | null
  status: string
  effectiveDate: string | null
  created: string | null
  updated: string | null
  agentName: string | null
  assignedToName: string | null
  holderName: string | null
  holderPhone: string | null
  stateCode: string | null
  zipCode: string | null
  carrierName: string | null
  planName: string | null
  metalLevel: number | null
  premium: string | null
  npn: string | null
  applicants: number | null
  isDuplicated: boolean | null
  stateDocs: string | null
  documentsDueDate: string | null
  paymentStatus: string | null
  carrierStatusGeneric: string | null
  carrierStatusRaw: string | null
  carrierIsAgeIn: boolean | null
  carrierIsDelinquent: boolean | null
}

interface Props {
  items: FamilyItem[]
  currentPk: number
  onNavigate: (pk: number) => void
}

function sortByHierarchy(items: FamilyItem[]): FamilyItem[] {
  const map = new Map<number, FamilyItem[]>()
  const roots: FamilyItem[] = []
  items.forEach((item) => {
    const parentExists = items.some((i) => i.pk === item.parentPk)
    if (item.parentPk === null || !parentExists) {
      roots.push(item)
    } else {
      const siblings = map.get(item.parentPk) ?? []
      siblings.push(item)
      map.set(item.parentPk, siblings)
    }
  })
  const result: FamilyItem[] = []
  function walk(nodes: FamilyItem[]) {
    nodes.sort((a, b) => a.pk - b.pk).forEach((node) => {
      result.push(node)
      walk(map.get(node.pk) ?? [])
    })
  }
  walk(roots)
  return result
}

function carrierStatusBg(status: string | null) {
  if (!status) return 'secondary'
  const map: Record<string, string> = {
    IN_FORCE: 'success',
    TERMINATED: 'danger',
    DELINQUENT: 'warning',
    PAST_DUE: 'warning',
    GRACE_PERIOD: 'warning',
    PENDING_EFFECTUATION: 'info',
  }
  return map[status] ?? 'secondary'
}

export const PolicyFamilyTree: React.FC<Props> = ({items, currentPk, onNavigate}) => {
  const sorted = sortByHierarchy(items)
  const [previewId, setPreviewId] = useState<string | null>(null)
  const {data: statusesData} = useQuery(GET_ALL_POLICIES_STATUSES, {fetchPolicy: 'cache-first'})
  const statuses = statusesData?.allPolicyStatuses?.edges ?? []

  return (
    <div className='table-responsive'>
      <table className='table table-hover table-sm align-middle mb-0' style={{whiteSpace: 'nowrap'}}>
        <thead className='table-light'>
          <tr>
            <th className='fw-bold'>#</th>
            <th className='fw-bold'>Agent</th>
            <th className='fw-bold'>Client</th>
            <th className='fw-bold'>Policy</th>
            <th className='fw-bold'>Status</th>
            <th className='fw-bold'>Assigned To</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((item) => {
            const isCurrent = item.pk === currentPk
            const tooltipId = `fam-tooltip-${item.pk}`
            return (
              <tr
                key={item.pk}
                style={{background: isCurrent ? 'rgba(var(--bs-primary-rgb), 0.06)' : undefined}}
              >
                {/* # + indentación */}
                <td>
                  <div
                    className='d-flex align-items-center gap-1'
                    style={{paddingLeft: `${item.depth * 18}px`}}
                  >
                    {item.depth > 0 && (
                      <span style={{color: '#bbb', fontSize: '0.75rem', lineHeight: 1}}>└</span>
                    )}
                    <span className='fw-semibold' style={{fontSize: '0.82rem'}}>
                      #{item.pk}
                    </span>
                    {isCurrent && (
                      <Badge bg='warning' text='dark' style={{fontSize: '0.62rem', fontWeight: 600}}>
                        current
                      </Badge>
                    )}
                  </div>
                </td>

                {/* Agent */}
                <td style={{fontSize: '0.82rem', maxWidth: '130px', wordBreak: 'break-word', whiteSpace: 'normal', lineHeight: '1.3'}}>
                  {item.agentName || '—'}
                </td>

                {/* Client (holder name + policy name + phone + state/zip) */}
                <td style={{maxWidth: '200px', minWidth: '160px', wordBreak: 'break-word', whiteSpace: 'normal', lineHeight: '1.3'}}>
                  <div className='fw-bold text-primary' style={{fontSize: '0.85rem'}}>
                    {item.policyName || item.holderName || '—'}
                  </div>
                  {item.holderPhone && (
                    <div style={{fontSize: '0.78rem', color: '#666'}}>
                      <i className='bi bi-telephone-fill me-1' />
                      {item.holderPhone}
                    </div>
                  )}
                  {(item.stateCode || item.zipCode) && (
                    <div style={{fontSize: '0.78rem', color: '#888'}}>
                      {item.stateCode} {item.zipCode}
                    </div>
                  )}
                </td>

                {/* Policy (carrier + effective + applicants) */}
                <td style={{maxWidth: '200px'}}>
                  <div className='info-policy-name'>
                    <span
                      className='text-primary fw-bold'
                      style={{cursor: 'default', fontSize: '0.85rem'}}
                      data-tooltip-id={tooltipId}
                    >
                      {item.carrierName || 'N/R'}
                    </span>
                    {item.effectiveDate && (
                      <p className='m-0 p-0' style={{fontSize: '0.78rem'}}>
                        Effective: {moment(item.effectiveDate).format('MMM DD, YYYY')}
                      </p>
                    )}
                    <div className='d-flex align-items-center gap-1 mt-1 flex-wrap'>
                      <Badge
                        bg='primary'
                        className='d-flex align-items-center gap-1 text-white'
                        title={`${item.applicants ?? 0} Applicant(s)`}
                        style={{fontSize: '0.7rem'}}
                      >
                        <i className='bi bi-people-fill' />
                        {item.applicants ?? 0}
                      </Badge>
                    </div>
                    <Tooltip
                      id={tooltipId}
                      clickable
                      delayHide={400}
                      place='top'
                      className='tooltip-tfi tooltip-clean'
                      style={{maxWidth: '320px'}}
                    >
                      <div className='tooltip-content' style={{whiteSpace: 'normal', wordBreak: 'break-word'}}>
                        <h6 className='text-center mb-2'>More information</h6>
                        <div><strong>Plan:</strong> {item.planName || 'N/R'}</div>
                        <div>
                          <strong>Metal:</strong>{' '}
                          {METAL_LEVELS.find((m) => m.value === String(item.metalLevel))?.label || 'N/A'}
                        </div>
                        <div><strong>Premium:</strong> ${item.premium ?? '0'}</div>
                        <div><strong>NPN:</strong> {item.npn || 'N/A'}</div>
                        <div><strong>Is duplicate:</strong> {item.isDuplicated ? '✅ Yes' : '❌ No'}</div>
                        {item.created && (
                          <div><strong>Submitted:</strong> {moment(item.created).format('DD MMM, YY HH:mm')}</div>
                        )}
                        {item.updated && (
                          <div><strong>Last update:</strong> {moment(item.updated).format('DD MMM, YY HH:mm')}</div>
                        )}
                      </div>
                    </Tooltip>
                  </div>
                </td>

                {/* Status (policy + docs + payment + carrier) */}
                <td style={{minWidth: '140px'}}>
                  <BadgeStatePolicy
                    status={getTitleByStatus(statuses, item.status)}
                    color={getColorByStatus(statuses, item.status)}
                  />
                  <span className='d-block mt-1' style={{fontSize: '0.78rem'}}>
                    Documents:{' '}
                    <Badge className='badge-sm' bg={getDocumentVariant(item.stateDocs ?? undefined)}>
                      {item.stateDocs || 'N/A'}
                    </Badge>
                  </span>
                  {item.documentsDueDate && (
                    <span className='d-block' style={{fontSize: '0.78rem'}}>
                      Due:{' '}
                      <Badge
                        className='badge-sm'
                        bg={moment(item.documentsDueDate).isBefore(moment(), 'day') ? 'danger' : 'warning'}
                        text={moment(item.documentsDueDate).isBefore(moment(), 'day') ? 'white' : 'dark'}
                      >
                        {moment(item.documentsDueDate).format('MMM DD, YY')}
                      </Badge>
                    </span>
                  )}
                  <span className='d-block' style={{fontSize: '0.78rem'}}>
                    Payment:{' '}
                    <Badge
                      className='badge-sm'
                      bg={getPaymentVariant(item.paymentStatus ?? undefined)}
                      text={getPaymentVariant(item.paymentStatus ?? undefined) === 'danger' ? 'white' : undefined}
                    >
                      {item.paymentStatus || 'N/A'}
                    </Badge>
                  </span>
                  {item.carrierStatusGeneric && (
                    <span className='d-block' style={{fontSize: '0.78rem'}}>
                      Carrier:{' '}
                      <Badge
                        className='badge-sm'
                        bg={carrierStatusBg(item.carrierStatusGeneric)}
                        text={['DELINQUENT', 'PAST_DUE', 'GRACE_PERIOD'].includes(item.carrierStatusGeneric) ? 'dark' : undefined}
                      >
                        {item.carrierStatusRaw || item.carrierStatusGeneric}
                      </Badge>
                      {item.carrierIsAgeIn && (
                        <Badge className='badge-sm ms-1' bg='info' text='white'>AGE-IN</Badge>
                      )}
                      {item.carrierIsDelinquent && item.carrierStatusGeneric !== 'DELINQUENT' && (
                        <Badge className='badge-sm ms-1' bg='warning' text='dark'>DLQ</Badge>
                      )}
                    </span>
                  )}
                </td>

                {/* Assigned To */}
                <td style={{fontSize: '0.82rem', maxWidth: '130px', wordBreak: 'break-word', whiteSpace: 'normal', lineHeight: '1.3'}}>
                  {item.assignedToName || '—'}
                </td>

                {/* Preview */}
                <td>
                  <button
                    className={`btn btn-sm ${isCurrent ? 'btn-secondary' : 'btn-light-primary'}`}
                    style={{fontSize: '0.75rem', padding: '2px 8px'}}
                    disabled={isCurrent}
                    onClick={() => !isCurrent && setPreviewId(String(item.pk))}
                  >
                    <i className='bi bi-eye me-1' />
                    Preview
                  </button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>

      {previewId && (
        <ModalPreview
          id={previewId}
          isOpen={!!previewId}
          onClose={() => setPreviewId(null)}
        />
      )}
    </div>
  )
}
